/**
 * Pure helpers for the Dispatch filters panel: option lists and match tests
 * over the flat weekly WorkScopeAspect rows from {@link WeeklyContext}
 * (`row.job`, `row.scope`, `row.worker_assignment`, `row.date`).
 *
 * Generic option helpers (merge/effective-id logic) live in
 * `features/planning/shared/filters/planningFilterModel.js`.
 * Text search uses shared {@link itemMatchesSearchQuery} (same as Schedule).
 * Skipped aspects are omitted from Dispatch; they stay stored and visible on Schedule.
 */

import { getWorkerDisplayLabel } from "../../../utils/business";
import {
  formatScopeNotesWithExtraSections,
  scopeAspectHasNotes,
} from "../../../utils/business/scopes";
import { getUserDisplayName } from "../../../utils/business/user";
import { itemMatchesSearchQuery } from "../../../utils/common/searchFilter";
import {
  collectUniqueFilterOptions,
  filterOptionIdsMatchingSearch,
} from "../shared/filters/planningFilterModel";

/**
 * @typedef {import("../shared/filters/planningFilterModel").PlanningFilterOption} PlanningFilterOption
 */

/**
 * Detail assignment kinds on a WorkScopeAspect row (`row.concrete`, etc.).
 * Notes use a separate Has Notes checkbox (not this multi-select).
 */
export const DISPATCH_DETAIL_KINDS = [
  "concrete",
  "equipment",
  "subcontractor",
  "dumping",
];

/** Fixed Details filter options (Has Concrete / Equipment / Subcontractor / Dumping). */
export const DISPATCH_DETAIL_KIND_OPTIONS = [
  { value: "concrete", label: "Has Concrete" },
  { value: "equipment", label: "Has Equipment" },
  { value: "subcontractor", label: "Has Subcontractor" },
  { value: "dumping", label: "Has Dumping" },
];

/**
 * Display name for one detail assignment (mix design #, asset name, or contractor).
 *
 * @param {'concrete'|'equipment'|'subcontractor'|'dumping'} kind
 * @param {Object} item Assignment row from the aspect serializer.
 * @returns {string}
 */
export function getDispatchDetailItemName(kind, item) {
  if (!item) {
    return "";
  }
  if (kind === "concrete") {
    return item.mix?.design_number?.toString().trim() || "";
  }
  if (kind === "equipment") {
    return (
      item.asset?.name?.toString().trim() ||
      item.equipment?.name?.toString().trim() ||
      ""
    );
  }
  return item.contractor?.name?.toString().trim() || "";
}

/**
 * Whether a row has at least one assignment of the given detail kind.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {string} kind Detail kind key.
 * @returns {boolean}
 */
export function dispatchRowHasDetailKind(row, kind) {
  return (row?.[kind]?.length ?? 0) > 0;
}

/**
 * Whether a WorkScopeAspect should appear in Dispatch.
 * Skipped rows stay in the database and on the Schedule timeline.
 *
 * @param {Object|null|undefined} row WorkScopeAspect row.
 * @returns {boolean}
 */
export function isDispatchVisibleAspect(row) {
  return row?.skipped !== true;
}

/**
 * Detail kinds present on any of the given rows (for cross-curating Has-* options).
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchDetailKindOptions(rows) {
  const present = new Set();
  for (const row of rows ?? []) {
    for (const kind of DISPATCH_DETAIL_KINDS) {
      if (dispatchRowHasDetailKind(row, kind)) {
        present.add(kind);
      }
    }
  }
  return DISPATCH_DETAIL_KIND_OPTIONS.filter((option) =>
    present.has(option.value)
  );
}

/**
 * Normalize a job/API time string to `HH:MM` for start-time filter values/labels.
 *
 * @param {string|null|undefined} time Raw time (e.g. `"07:00:00"` or `"07:00"`).
 * @returns {string} Normalized `HH:MM`, or `""` when missing/invalid.
 */
export function normalizeDispatchStartTime(time) {
  if (!time || typeof time !== "string") {
    return "";
  }
  const trimmed = time.trim();
  if (trimmed.length >= 5) {
    return trimmed.slice(0, 5);
  }
  return trimmed;
}

/**
 * Map weekly jobs to project filter options, sorted by job-number label.
 *
 * @param {Array<Object>} jobs Weekly dispatch jobs.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchProjectOptions(jobs) {
  return collectUniqueFilterOptions(
    (jobs ?? []).map((job) => ({
      value: job.id,
      label: `${job.number} — ${job.description}`,
    }))
  );
}

/**
 * Unique work scopes on rows, sorted by title. Rows with no scope set are
 * skipped (they cannot be targeted by the scope filter).
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchScopeOptions(rows) {
  return collectUniqueFilterOptions(
    (rows ?? []).map((row) =>
      row?.scope?.id == null
        ? null
        : {
            value: row.scope.id,
            label: row.scope.title?.trim() || String(row.scope.id),
          }
    )
  );
}

/**
 * Unique workers assigned on rows, sorted by display name.
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchWorkerOptions(rows) {
  return collectUniqueFilterOptions(
    (rows ?? []).flatMap((row) =>
      (row?.worker_assignment ?? []).map((worker) =>
        worker?.id == null
          ? null
          : { value: worker.id, label: getWorkerDisplayLabel(worker) }
      )
    )
  );
}

/**
 * Unique project managers on weekly jobs, sorted by display name.
 *
 * @param {Array<Object>} jobs Weekly dispatch jobs.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchManagerOptions(jobs) {
  return collectUniqueFilterOptions(
    (jobs ?? []).flatMap((job) =>
      (job?.managers ?? []).map((manager) => {
        const label = getUserDisplayName(manager, "");
        if (manager?.id == null || !label) {
          return null;
        }
        return { value: manager.id, label };
      })
    )
  );
}

/**
 * Unique project default start times on weekly jobs (`HH:MM`).
 *
 * @param {Array<Object>} jobs Weekly dispatch jobs.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchStartTimeOptions(jobs) {
  return collectUniqueFilterOptions(
    (jobs ?? []).map((job) => {
      const time = normalizeDispatchStartTime(job?.default_time);
      return time ? { value: time, label: time } : null;
    })
  );
}

/**
 * True when the row's job is in the selected project ids (empty = all).
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} projectIds Selected project (job) ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesProjectFilter(row, projectIds) {
  if (!projectIds?.length) {
    return true;
  }
  return projectIds.includes(String(row?.job?.id));
}

/**
 * True when the row's catalog work scope is in the selected scope ids (empty = all).
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} scopeIds Selected work-scope ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesScopeFilter(row, scopeIds) {
  if (!scopeIds?.length) {
    return true;
  }
  return scopeIds.includes(String(row?.scope?.id));
}

/**
 * True when any assigned worker is in the selected worker ids (empty = all).
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} workerIds Selected worker ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesWorkerFilter(row, workerIds) {
  if (!workerIds?.length) {
    return true;
  }
  return (row?.worker_assignment ?? []).some((worker) =>
    workerIds.includes(String(worker?.id))
  );
}

/**
 * Whether a weekly job matches the selected manager filter (OR any selected).
 *
 * @param {Object} job Weekly dispatch job.
 * @param {Array<string>} managerIds Selected manager ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchJobMatchesManagerFilter(job, managerIds) {
  if (!managerIds?.length) {
    return true;
  }
  return (job?.managers ?? []).some((manager) =>
    managerIds.includes(String(manager?.id))
  );
}

/**
 * True when the row's job matches the selected manager filter (empty = all).
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} managerIds Selected manager ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesManagerFilter(row, managerIds) {
  return dispatchJobMatchesManagerFilter(row?.job, managerIds);
}

/**
 * Whether a weekly job matches the selected start-time filter (project default).
 *
 * @param {Object} job Weekly dispatch job.
 * @param {Array<string>} startTimes Selected `HH:MM` values; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchJobMatchesStartTimeFilter(job, startTimes) {
  if (!startTimes?.length) {
    return true;
  }
  const time = normalizeDispatchStartTime(job?.default_time);
  return Boolean(time) && startTimes.includes(time);
}

/**
 * True when the row's job default start time is in the selected times (empty = all).
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} startTimes Selected `HH:MM` values; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesStartTimeFilter(row, startTimes) {
  return dispatchJobMatchesStartTimeFilter(row?.job, startTimes);
}

/**
 * Whether a row has any of the selected detail kinds (OR). Empty selection = all.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} detailKinds Selected kind keys; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesDetailsFilter(row, detailKinds) {
  if (!detailKinds?.length) {
    return true;
  }
  return detailKinds.some((kind) => dispatchRowHasDetailKind(row, kind));
}

/**
 * Whether a row matches the Has Notes filter and optional Notes Contain text.
 * Searches primary notes plus extra_data_sections; inactive when hasNotes is false.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Object} notesFilter
 * @param {boolean} [notesFilter.hasNotes=false]
 * @param {string} [notesFilter.notesContain=""]
 * @returns {boolean}
 */
export function dispatchRowMatchesNotesFilter(
  row,
  { hasNotes = false, notesContain = "" } = {}
) {
  if (!hasNotes) {
    return true;
  }
  if (!scopeAspectHasNotes(row)) {
    return false;
  }
  const query = notesContain?.trim();
  if (!query) {
    return true;
  }
  const haystack = formatScopeNotesWithExtraSections(
    row.notes,
    row.extra_data_sections
  ).toLowerCase();
  return haystack.includes(query.toLowerCase());
}

/**
 * Collect searchable detail display names on a row.
 * When `detailKinds` is non-empty, only those kinds are scanned so Details
 * filter + TopBar search compose (e.g. Has Concrete + "604c").
 * Notes are never included in TopBar search.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>|null} [detailKinds=null] Limit to these kinds; null/[] = all.
 * @returns {string[]}
 */
export function collectDispatchDetailSearchLabels(row, detailKinds = null) {
  const kinds =
    detailKinds?.length > 0 ? detailKinds : DISPATCH_DETAIL_KINDS;
  const labels = [];
  for (const kind of kinds) {
    for (const item of row?.[kind] ?? []) {
      const name = getDispatchDetailItemName(kind, item);
      if (name) {
        labels.push(name);
      }
    }
  }
  return labels;
}

/**
 * Whether any detail name on the row matches the search query.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {string} query Raw search text.
 * @param {Array<string>|null} [detailKinds=null] Optional kind limit (see collect).
 * @returns {boolean}
 */
export function dispatchRowMatchesDetailsSearch(row, query, detailKinds = null) {
  if (!query?.trim()) {
    return true;
  }
  const normalized = query.toLowerCase();
  return collectDispatchDetailSearchLabels(row, detailKinds).some((label) =>
    label.toLowerCase().includes(normalized)
  );
}

/**
 * @typedef {Object} DispatchFilterSelection
 * @property {Array<string>} [projectIds]
 * @property {Array<string>} [scopeIds]
 * @property {Array<string>} [workerIds]
 * @property {Array<string>} [managerIds]
 * @property {Array<string>} [startTimes]
 * @property {Array<string>} [detailKinds]
 * @property {boolean} [hasNotes]
 * @property {string} [notesContain]
 */

/**
 * Whether a row matches all active filter dimensions (AND), optionally omitting one
 * for cross-curated option lists.
 *
 * @param {Object} row WorkScopeAspect row.
 * @param {DispatchFilterSelection} filters
 * @param {Object} [options]
 * @param {'project'|'scope'|'worker'|'manager'|'startTime'|'details'|'notes'|null} [options.omit]
 * @returns {boolean}
 */
export function dispatchRowMatchesFilters(row, filters = {}, { omit = null } = {}) {
  if (omit !== "project" && !dispatchRowMatchesProjectFilter(row, filters.projectIds)) {
    return false;
  }
  if (omit !== "scope" && !dispatchRowMatchesScopeFilter(row, filters.scopeIds)) {
    return false;
  }
  if (omit !== "worker" && !dispatchRowMatchesWorkerFilter(row, filters.workerIds)) {
    return false;
  }
  if (
    omit !== "manager" &&
    !dispatchRowMatchesManagerFilter(row, filters.managerIds)
  ) {
    return false;
  }
  if (
    omit !== "startTime" &&
    !dispatchRowMatchesStartTimeFilter(row, filters.startTimes)
  ) {
    return false;
  }
  if (
    omit !== "details" &&
    !dispatchRowMatchesDetailsFilter(row, filters.detailKinds)
  ) {
    return false;
  }
  if (
    omit !== "notes" &&
    !dispatchRowMatchesNotesFilter(row, {
      hasNotes: filters.hasNotes,
      notesContain: filters.notesContain,
    })
  ) {
    return false;
  }
  return true;
}

/**
 * Keep rows matching all active filters (AND).
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @param {DispatchFilterSelection} filters
 * @returns {Array<Object>}
 */
export function filterDispatchScopeRows(rows, filters) {
  return (rows ?? []).filter((row) => dispatchRowMatchesFilters(row, filters));
}

/**
 * Whether a weekly job matches TopBar search by number/description or manager label.
 *
 * @param {Object} job Weekly dispatch job.
 * @param {string} query Raw search text.
 * @param {Array<string>} [matchingManagerIds] Precomputed manager ids whose labels match.
 * @returns {boolean}
 */
export function dispatchJobMatchesSearch(job, query, matchingManagerIds = null) {
  if (itemMatchesSearchQuery(job, query, ["number", "description"])) {
    return true;
  }
  const managerIds =
    matchingManagerIds ??
    filterOptionIdsMatchingSearch(collectDispatchManagerOptions([job]), query);
  return (
    managerIds.length > 0 && dispatchJobMatchesManagerFilter(job, managerIds)
  );
}

/**
 * Whether a WorkScopeAspect row matches search by scope title or job fields.
 *
 * @param {Object} row Weekly WorkScopeAspect row.
 * @param {string} query Raw search text.
 * @returns {boolean}
 */
export function dispatchRowMatchesTextSearch(row, query) {
  return itemMatchesSearchQuery(row, query, [
    "scope.title",
    "job.number",
    "job.description",
  ]);
}

/**
 * Keep rows that match job/scope text, worker/manager labels, or detail names.
 * When `detailKinds` is set (active Details filter), detail-name search only
 * scans those kinds so Has Concrete + "604c" narrows to matching concrete.
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows (already toolbar-filtered).
 * @param {string} query Raw search text.
 * @param {Array<Object>} [jobs] Weekly jobs for manager options (defaults to jobs on rows).
 * @param {Array<string>} [detailKinds] Active Details filter kinds (optional).
 * @returns {Array<Object>}
 */
export function filterDispatchScopeRowsForSearch(
  rows,
  query,
  jobs = null,
  detailKinds = null
) {
  if (!query?.trim()) {
    return rows ?? [];
  }
  const managerSourceJobs =
    jobs ??
    (() => {
      const byId = new Map();
      for (const row of rows ?? []) {
        if (row?.job?.id != null && !byId.has(row.job.id)) {
          byId.set(row.job.id, row.job);
        }
      }
      return [...byId.values()];
    })();
  const matchingWorkerIds = filterOptionIdsMatchingSearch(
    collectDispatchWorkerOptions(rows),
    query
  );
  const matchingManagerIds = filterOptionIdsMatchingSearch(
    collectDispatchManagerOptions(managerSourceJobs),
    query
  );
  return (rows ?? []).filter(
    (row) =>
      dispatchRowMatchesTextSearch(row, query) ||
      (matchingWorkerIds.length > 0 &&
        dispatchRowMatchesWorkerFilter(row, matchingWorkerIds)) ||
      (matchingManagerIds.length > 0 &&
        dispatchRowMatchesManagerFilter(row, matchingManagerIds)) ||
      dispatchRowMatchesDetailsSearch(row, query, detailKinds)
  );
}
